import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { ConfiguracionContextService } from '../../context/configuracion-context.service';
import { ConfiguracionService } from '../../services/configuracion.service';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-disenyo-etiquetas',
    templateUrl: './disenyo-etiquetas.component.html'
})
export class MantenimientoDisenyoEtiquetasComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = false;
    public filtersForm: UntypedFormGroup;

    public etiquetas;

    public disenyoEtiquetaId;
    public imagen;
    public filename;

    public newEditModal = false;
    public deleteModal = false;
    public uploadModal = false;

    public mensajes = new Array<Message>();
    public showError: boolean;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _sessionContext: SessionContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _configuracionContext: ConfiguracionContextService,
        private readonly _configuracionService: ConfiguracionService,
        private readonly utilsService: UtilsService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            nombre: [''],
            tipoConfeccion: [''],
            fechaInicio: [''],
            fechaFin: [''],
            dateType: ['false']
        });

        this._configuracionContext.disenyoEtiquetasFilters = null;

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.getDisenyoEtiquetas();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get tiposConfeccion() {
        return this._dataProvider.tiposConfeccion;
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    public pageChange(page) {
        this._configuracionContext.disenyoEtiquetasFilters.numPagina = this.currentPage = page - 1;
        this.getDisenyoEtiquetas();
    }

    public itemsPerPageChange(items) {
        this._configuracionContext.disenyoEtiquetasFilters.numPagina = this.currentPage = 0;
        this._configuracionContext.disenyoEtiquetasFilters.tamPagina = this.itemsPerPage = items;
        this.getDisenyoEtiquetas();
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').value) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').value) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio'});
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return (this.filtersForm.get('nombre').value ||
            this.filtersForm.get('tipoConfeccion').value || this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.reset();
        this.filtersForm.get('dateType').patchValue('false');
    }

    public filtrar() {
        this.validDates();
        
        if (this.filtersForm.valid) {
            this.applyFilters();
        }
    }

    private applyFilters() {
        this.currentPage = 0;
        this._configuracionContext.disenyoEtiquetasFilters = {
            desDisenyoEtiqueta: this.filtersForm.get('nombre').value ? this.filtersForm.get('nombre').value : '',
            tipoConfeccionId: this.filtersForm.get('tipoConfeccion').value ? this.filtersForm.get('tipoConfeccion').value.value : '',
            includeFile: true,
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            fechaActualizacion: this.filtersForm.get('dateType').value,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getDisenyoEtiquetas();
    }

    public getDisenyoEtiquetas() {
        if (this._configuracionContext.disenyoEtiquetasFilters) {
            this._filters = this._configuracionContext.disenyoEtiquetasFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._configuracionService.getDisenyoEtiquetas(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.etiquetas = resp.lista;
        }, (error) => {
            this.pushMessage('global.error.noResponse');
        });
    }

    public create() {
        this.disenyoEtiquetaId = null;
        this.newEditModal = true;
    }

    public update(disenyoEtiqueta) {
        this.disenyoEtiquetaId = disenyoEtiqueta.id;
        this.newEditModal = true;
    }

    public openImagen(disenyoEtiqueta) {
        this.disenyoEtiquetaId = disenyoEtiqueta.id;
        this.imagen = disenyoEtiqueta.archivo ? 'data:image/jpeg;base64,' + disenyoEtiqueta.archivo : null;
        this.filename = disenyoEtiqueta.des ? disenyoEtiqueta.des : 'download';
        this.uploadModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._configuracionService.deleteDisenyoEtiqueta(this.disenyoEtiquetaId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then(() => {
            this.close();
            this.pushMessage('global.success.itemDeleted');
            this.getDisenyoEtiquetas();
        });
    }

    public askDelete(disenyoEtiqueta) {
        this.disenyoEtiquetaId = disenyoEtiqueta.id;
        this.deleteModal = true;
    }

    public close() {
        this.deleteModal = false;
        this.newEditModal = false;
        this.uploadModal = false;
    }

    public refresh(value) {
        this.newEditModal = false;
        this.uploadModal = false;
        if (value) {
            this.getDisenyoEtiquetas();
        }
    }

    public refreshReferencia(value) {
        if (value) {
            this._spinnerService.showSpinner();
            this._configuracionService.getDisenyoEtiquetas(this._filters).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this.totalItems = resp.paginacionMeta.totalCount;
                this.etiquetas = resp.lista;
                this.imagen = resp.lista.find(x => x.id === this.disenyoEtiquetaId).archivo ?
                    'data:image/jpeg;base64,' + resp.lista.find(x => x.id === this.disenyoEtiquetaId).archivo : null;
                this.filename = resp.lista.find(x => x.id === this.disenyoEtiquetaId).archivo ?
                    resp.lista.find(x => x.id === this.disenyoEtiquetaId).des : 'download';
            });
        } else {
            this.imagen = null;
            this.filename = null;
            this.getDisenyoEtiquetas();
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        if (mensaje.includes('error') || mensaje.includes('ApiErrors')) {
            this.mensajes.push({ class: 'error', message: mensaje });
        } else {
            this.mensajes.push({ class: 'success', message: mensaje });
        }
    }

    public getIcon(prop: string) {
      let iconClass = 'fa fa-sort';
      if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
          iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
      }
      return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
      this.columnId = idColumn;
      if (this.etiquetas && this.etiquetas.length > 0) {
        if (this._configuracionContext.disenyoEtiquetasFilters) {
          this._filters = this._configuracionContext.disenyoEtiquetasFilters;
          if (!this.orderBy || this.orderBy.includes('DESC')) {
            this.orderBy = 'ASC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: false
              }
            ];
          } else if (this.orderBy.includes('ASC')) {
            this.orderBy = 'DESC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: true
              }
            ];
          }
          this.getDisenyoEtiquetas();
        }
      }
    }

    public trackByEtiquetaId(index: number, etiqueta: { id: number }): number {
      return etiqueta.id;
    }

}
